import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, Rocket, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";

const STEPS = [
  { n: 1, code: "1", key: "define", label: "Define", phase: "OPERATIONS", placeholder: "Problem statement, business case, defect metrics and project scope boundaries." },
  { n: 2, code: "2", key: "measure", label: "Measure", phase: "OPERATIONS", placeholder: "Performance baselines — current cycle times, defect rates, or performance deltas." },
  { n: 3, code: "3", key: "analyse", label: "Analyse", phase: "OPERATIONS", placeholder: "Root-cause analysis — structured 5-Whys or fishbone categories mapping systemic drivers." },
  { n: 4, code: "4", key: "improve", label: "Improve", phase: "OPERATIONS" },
  { n: 5, code: "5", key: "control", label: "Control", phase: "OPERATIONS", placeholder: "Verification metrics, ownership parameters and standard guardrails to sustain the fix." },
];
const LAST = STEPS.length;
const uid = () => "imp_" + Math.random().toString(36).slice(2, 10);
const empty = { define: "", measure: "", analyse: "", improveTasks: [], control: "" };
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export default function Dmaic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData({ ...empty, ...(res.data.payload || {}), improveTasks: res.data.payload?.improveTasks || [] }); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled DMAIC Project", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const setText = (v) => setData((d) => ({ ...d, [cur.key]: v }));
  const addTask = () => setData((d) => ({ ...d, improveTasks: [...d.improveTasks, { id: uid(), name: "", status: false }] }));
  const setTask = (i, name) => setData((d) => ({ ...d, improveTasks: d.improveTasks.map((t, j) => (j === i ? { ...t, name } : t)) }));
  const removeTask = (i) => setData((d) => ({ ...d, improveTasks: d.improveTasks.filter((_, j) => j !== i) }));

  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const launch = async () => {
    if (!data.improveTasks.some((t) => t.name.trim())) { toast.error("Add at least one Improve action to track"); setStep(4); return; }
    if (await persist({ status: "Active Execution" })) { toast.success("DMAIC project launched"); navigate(`/dmaic/${id}/track`); }
  };
  const applySuggestion = (text) => {
    if (cur.key === "improve") setData((d) => ({ ...d, improveTasks: [...d.improveTasks, { id: uid(), name: text, status: false }] }));
    else setData((d) => { const e = d[cur.key]; return { ...d, [cur.key]: !e || !e.trim() ? text : `${e.trimEnd()}\n- ${text}` }; });
    toast.success("Suggestion added");
  };
  const onPull = ({ title: t, context }) => { setData((d) => ({ ...d, define: context || d.define })); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="dmaic-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="DMAIC project title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 grid grid-cols-5 gap-2" data-testid="dmaic-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-base font-bold block ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>{s.label[0]}</span>
              <p className={`text-[11px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          {cur.key === "improve" ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Define the optimisation actions to eliminate the root causes. These become your live tracking checklist.</p>
              <button data-testid="dmaic-add-task" onClick={addTask} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add action</button>
              <div className="space-y-2">
                {data.improveTasks.map((t, i) => (
                  <div key={t.id} className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400 w-5">{i + 1}.</span>
                    <input data-testid={`dmaic-task-input-${i}`} value={t.name} onChange={(e) => setTask(i, e.target.value)} placeholder="Improvement action…" className={inputCls + " flex-1"} />
                    <button data-testid={`dmaic-remove-task-${i}`} onClick={() => removeTask(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                  </div>
                ))}
                {data.improveTasks.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No actions yet.</p>}
              </div>
            </div>
          ) : (
            <textarea data-testid={`field-${cur.key}`} rows={9} className={taCls} value={data[cur.key]} onChange={(e) => setText(e.target.value)} placeholder={cur.placeholder} />
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="dmaic-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="dmaic-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="dmaic-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next Step <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="dmaic-launch" onClick={launch} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800"><Rocket size={18} weight="fill" /> Launch Tracking</button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="dmaic" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
